import { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { Header } from '../components/Header';
import { KPICard } from '../components/KPICard';
import { DealTable } from '../components/DealTable';
import { EscalationAlerts } from '../components/EscalationAlerts';
import { getDeals, getDashboardStats, getPinnedNotes, adminDeleteNote, getUsers } from '../lib/api';
import { toast } from 'sonner';
import { useWsRefetch } from '../contexts/WebSocketContext';
import { useAuth } from '../contexts/AuthContext';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Button } from '../components/ui/button';
import { DollarSign, Clock, FileText, AlertTriangle, TrendingUp, Banknote, Pin, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export const DashboardPage = () => {
  const navigate = useNavigate();
  const [deals, setDeals] = useState([]);
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pinnedNotes, setPinnedNotes] = useState([]);
  const [deletingNoteId, setDeletingNoteId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [filters, setFilters] = useState({
    manager: '',
    lender: '',
    aging: '',
    risk: '',
    role: '',
    roleUser: '',
  });
  const [lastUpdated, setLastUpdated] = useState(null);
  const [usersList, setUsersList] = useState([]);

  const loadData = useCallback(async () => {
    try {
      const params = {};
      if (filters.manager) params.manager = filters.manager;
      if (filters.lender) params.lender = filters.lender;
      if (filters.aging) params.aging = filters.aging;
      if (filters.risk) params.risk = filters.risk;
      if (filters.role && filters.roleUser) {
        params.role_filter = filters.role;
        params.role_user = filters.roleUser;
      }
      // Exclude funded deals from dashboard escalation view
      params.include_funded = false;

      // Send client's local date so "today" calculations match the user's timezone
      const localToday = new Date();
      params.client_today = `${localToday.getFullYear()}-${String(localToday.getMonth()+1).padStart(2,'0')}-${String(localToday.getDate()).padStart(2,'0')}`;

      const [dealsRes, statsRes, pinnedRes] = await Promise.all([
        getDeals(params),
        getDashboardStats(params),
        getPinnedNotes(),
      ]);

      setDeals(dealsRes.data);
      setStats(statsRes.data);
      setPinnedNotes(pinnedRes.data || []);
      setLastUpdated(new Date().toISOString());
    } catch (err) {
      toast.error('Failed to load dashboard data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Load users list for role filter
  useEffect(() => {
    getUsers().then(res => setUsersList((res.data || []).filter(u => u.is_active))).catch(() => {});
  }, []);

  // Real-time updates via WebSocket
  useWsRefetch([
    'deal_created', 'deal_updated', 'deal_deleted', 'deal_funded', 'deal_unfunded',
    'deal_archived', 'deal_unarchived', 'note_created', 'note_updated', 'note_deleted',
    'note_pinned', 'stip_verified', 'stip_uploaded', 'escalation_action',
  ], loadData);

  const handleDeleteNote = async (noteId) => {
    setDeletingNoteId(noteId);
    try {
      await adminDeleteNote(noteId);
      toast.success('Note deleted');
      setConfirmDeleteId(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete note');
    } finally {
      setDeletingNoteId(null);
    }
  };


  const handleRefresh = () => {
    setLoading(true);
    loadData();
    toast.success('Dashboard refreshed');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const today = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <Layout>
      <Header
        title="Daily Dashboard"
        subtitle={today}
        onRefresh={handleRefresh}
        lastUpdated={lastUpdated}
        onAddDeal={() => navigate('/deal/new')}
      />

      <div className="p-6 space-y-6" data-testid="dashboard-content">
        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <KPICard
            label="Total Cash at Risk"
            value={formatCurrency(stats?.total_cash_at_risk)}
            icon={TrendingUp}
            type="success"
            subtitle={`+${formatCurrency(stats?.booking_total_today || 0)} today`}
          />
          <KPICard
            label="Funding Total"
            value={formatCurrency(stats?.funding_total)}
            icon={Banknote}
            type="success"
            subtitle={`+${formatCurrency(stats?.funding_total_today || 0)} today`}
          />
          <KPICard
            label="Total Deals in Transit"
            value={stats?.deals_in_transit || 0}
            icon={FileText}
            type="default"
          />
          <KPICard
            label={`${stats?.thresholds?.critical || 10} Days and Older`}
            value={formatCurrency(stats?.cash_at_risk_10plus)}
            icon={DollarSign}
            type="urgent"
          />
          <KPICard
            label={`Critical Aging (${stats?.thresholds?.critical || 10}+ Days)`}
            value={stats?.deals_10plus_count || 0}
            icon={AlertTriangle}
            type="urgent"
          />
          <KPICard
            label="Missing Stipulations"
            value={stats?.missing_stips_total || 0}
            icon={Clock}
            type="warning"
          />
        </div>

        {/* Escalation Alerts */}
        <EscalationAlerts onRefresh={loadData} />

        {/* Aging Breakdown */}
        <div className="bg-white border border-slate-200 p-4">
          <h3 className="font-heading text-sm font-semibold uppercase tracking-wider text-slate-500 mb-4">
            Deal Aging Breakdown
          </h3>
          <div className="flex gap-4">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-emerald-500" />
              <span className="text-sm text-slate-600">0-{(stats?.thresholds?.warning || 3) - 1} Days:</span>
              <span className="font-mono font-bold">{stats?.aging_breakdown?.green || 0}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-amber-500" />
              <span className="text-sm text-slate-600">{stats?.thresholds?.warning || 3}-{(stats?.thresholds?.critical || 10) - 1} Days:</span>
              <span className="font-mono font-bold">{stats?.aging_breakdown?.yellow || 0}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-red-500" />
              <span className="text-sm text-slate-600">{stats?.thresholds?.critical || 10}+ Days:</span>
              <span className="font-mono font-bold">{stats?.aging_breakdown?.red || 0}</span>
            </div>
          </div>
        </div>

        {/* Pinned Notes */}
        {pinnedNotes.length > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 p-4" data-testid="pinned-notes-section">
            <div className="flex items-center gap-2 mb-3">
              <Pin size={16} className="text-yellow-700 fill-current" />
              <h3 className="font-heading text-sm font-bold uppercase tracking-tight text-yellow-800">
                Pinned Notes ({pinnedNotes.length})
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {pinnedNotes.map((note) => (
                <div
                  key={note.id}
                  className={`border p-3 relative group ${
                    note.note_type === 'admin_alert'
                      ? 'bg-red-50 border-red-300 ring-1 ring-red-200'
                      : 'bg-white border-yellow-200'
                  }`}
                  data-testid={`pinned-note-${note.id}`}
                >
                  <div className="cursor-pointer" onClick={() => navigate(`/deal/${note.deal_id}`)}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-bold text-slate-900 uppercase tracking-wider">
                        #{note.deal_number} - {note.customer_name}
                      </span>
                      <span className={`text-xs px-1.5 py-0.5 font-semibold uppercase ${
                        note.note_type === 'admin_alert' ? 'bg-red-200 text-red-800' :
                        note.note_type === 'lender_facing' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'
                      }`}>
                        {note.note_type === 'admin_alert' ? 'Admin Alert' : note.note_type === 'lender_facing' ? 'Lender' : 'Internal'}
                      </span>
                    </div>
                    <p className="text-sm text-slate-700 line-clamp-2">{note.note_text}</p>
                    <p className="text-xs text-slate-400 mt-1">
                      {note.created_by_name} - {new Date(note.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </p>
                  </div>
                  {/* Admin delete button */}
                  {isAdmin && (
                    confirmDeleteId === note.id ? (
                      <div className="mt-2 pt-2 border-t border-slate-200 flex items-center gap-2" data-testid={`confirm-delete-${note.id}`}>
                        <span className="text-xs text-red-600 font-medium">Delete this note?</span>
                        <button
                          onClick={() => handleDeleteNote(note.id)}
                          disabled={deletingNoteId === note.id}
                          className="text-xs bg-red-600 text-white px-2.5 py-1 rounded hover:bg-red-700 disabled:opacity-50"
                          data-testid={`confirm-delete-yes-${note.id}`}
                        >
                          {deletingNoteId === note.id ? 'Deleting...' : 'Yes, Delete'}
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(null); }}
                          className="text-xs text-slate-500 hover:text-slate-700 px-2 py-1"
                          data-testid={`confirm-delete-cancel-${note.id}`}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(note.id); }}
                        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-red-600 p-1"
                        title="Delete note"
                        data-testid={`delete-note-btn-${note.id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    )
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white border border-slate-200 p-4">
          <div className="flex flex-wrap items-center gap-4">
            <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
              Filters:
            </span>
            
            <Select value={filters.lender || "all"} onValueChange={(v) => setFilters({ ...filters, lender: v === "all" ? "" : v })}>
              <SelectTrigger className="w-40" data-testid="filter-lender">
                <SelectValue placeholder="Lender" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Lenders</SelectItem>
                {stats?.lenders?.map((l) => (
                  <SelectItem key={l} value={l}>{l}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filters.aging || "all"} onValueChange={(v) => setFilters({ ...filters, aging: v === "all" ? "" : v })}>
              <SelectTrigger className="w-40" data-testid="filter-aging">
                <SelectValue placeholder="Aging" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Ages</SelectItem>
                <SelectItem value="green">0-{(stats?.thresholds?.warning || 3) - 1} Days</SelectItem>
                <SelectItem value="yellow">{stats?.thresholds?.warning || 3}-{(stats?.thresholds?.critical || 10) - 1} Days</SelectItem>
                <SelectItem value="red">{stats?.thresholds?.critical || 10}+ Days</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filters.risk || "all"} onValueChange={(v) => setFilters({ ...filters, risk: v === "all" ? "" : v })}>
              <SelectTrigger className="w-40" data-testid="filter-risk">
                <SelectValue placeholder="Risk Score" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Risk Levels</SelectItem>
                <SelectItem value="green">Low Risk</SelectItem>
                <SelectItem value="yellow">Medium Risk</SelectItem>
                <SelectItem value="red">High Risk</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filters.role || "all"} onValueChange={(v) => setFilters({ ...filters, role: v === "all" ? "" : v, roleUser: '' })}>
              <SelectTrigger className="w-44" data-testid="filter-role">
                <SelectValue placeholder="Role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                <SelectItem value="staff">Sales Person</SelectItem>
                <SelectItem value="finance_manager">Finance Manager</SelectItem>
                <SelectItem value="desk_manager">Desk Manager</SelectItem>
                <SelectItem value="general_manager">General Manager</SelectItem>
                <SelectItem value="closer">Closer</SelectItem>
                <SelectItem value="admin">Administrator</SelectItem>
              </SelectContent>
            </Select>

            {filters.role && (
              <Select value={filters.roleUser || "all"} onValueChange={(v) => setFilters({ ...filters, roleUser: v === "all" ? "" : v })}>
                <SelectTrigger className="w-44" data-testid="filter-role-user">
                  <SelectValue placeholder="Select User" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All {filters.role === 'staff' ? 'Sales Persons' : filters.role === 'finance_manager' ? 'Finance Mgrs' : filters.role === 'desk_manager' ? 'Desk Mgrs' : filters.role === 'general_manager' ? 'Gen. Mgrs' : filters.role === 'closer' ? 'Closers' : 'Admins'}</SelectItem>
                  {usersList.filter(u => u.role === filters.role).map(u => (
                    <SelectItem key={u.id} value={u.name}>{u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {(filters.manager || filters.lender || filters.aging || filters.risk || filters.role) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setFilters({ manager: '', lender: '', aging: '', risk: '', role: '', roleUser: '' })}
                className="text-xs"
              >
                Clear Filters
              </Button>
            )}
          </div>
        </div>

        {/* Deals Table */}
        {loading ? (
          <div className="bg-white border border-slate-200 p-8 text-center">
            <p className="text-slate-500">Loading deals...</p>
          </div>
        ) : (
          <DealTable deals={deals} onDealFunded={loadData} pinnedNotes={pinnedNotes} thresholds={stats?.thresholds} />
        )}
      </div>
    </Layout>
  );
};
